const PROFILE_FIELDS = [
  { name: 'Shoulder Strap Width', aliases: ['Shoulder Width'] },
  { name: 'Full Shoulder Length', aliases: ['Shoulder Length'] },
  { name: 'Chest', aliases: [] },
  { name: 'Waist', aliases: [] },
  { name: 'Armpit / Armhole', aliases: ['Armpit'] },
  { name: 'Blouse Length', aliases: [] },
  { name: 'Sleeve Length', aliases: [] },
  { name: 'Sleeve Round', aliases: [] },
  { name: 'Front Neck Depth', aliases: [] },
  { name: 'Front Neck Width', aliases: [] },
  { name: 'Back Neck Depth', aliases: [] },
  { name: 'Back Neck Width', aliases: [] },
  { name: 'Front Neck Style', aliases: [] },
  { name: 'Back Neck Style', aliases: [] },
];

const OPTIONAL_PROFILE_FIELDS = [
  { name: 'Saree Finishing', aliases: [] },
];
const SAVED_PROFILE_FIELDS = [...PROFILE_FIELDS, ...OPTIONAL_PROFILE_FIELDS];

const PLACEMENT_ONLY_FIELDS = [
  { name: 'Saree Finishing', aliases: [] },
];

const MIN_VISIBLE_FIELDS = 2;
const PROFILE_STORAGE_KEY = 'geethas-blouse-profiles';
const FIELD_SELECTOR = [
  'input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="reset"]):not([type="radio"]):not([type="checkbox"]):not([type="file"])',
  'textarea',
  'select',
].join(',');
const PLACEMENT_FIELD_SELECTOR = [
  FIELD_SELECTOR,
  'input[type="radio"]',
  'input[type="checkbox"]',
  'button:not([type="submit"]):not([type="reset"])',
  '[role="radio"]',
  '[role="checkbox"]',
  '[role="combobox"]',
  '[role="option"]',
].join(',');

class BlouseMeasurements extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {MutationObserver | undefined} */
  #observer;

  /** @type {HTMLElement | null} */
  #saveAreaElement = null;

  /** @type {HTMLElement | null} */
  #saveAreaPlaceholder = null;

  /** @type {{name: string, measurements: Record<string, string>, savedAt?: string}[]} */
  #profiles = [];

  #syncQueued = false;

  /** @type {number | undefined} */
  #optionalRestoreFrame;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#saveAreaElement = /** @type {HTMLElement | null} */ (this.querySelector('[data-blouse-profile-save-area]'));
    this.#saveAreaPlaceholder = /** @type {HTMLElement | null} */ (this.querySelector('[data-blouse-profile-save-placeholder]'));
    this.#profiles = this.#loadProfiles();
    this.#renderProfileOptions();

    this.#profileSelect?.addEventListener('change', this.#handleProfileSelect, { signal });
    this.#profileNameInput?.addEventListener('keydown', this.#handleProfileNameKeydown, { signal });
    this.#saveButton?.addEventListener('click', this.#handleSaveProfile, { signal });
    this.#deleteButton?.addEventListener('click', this.#handleDeleteProfile, { signal });

    const root = this.#searchRoot;
    root.addEventListener('input', this.#scheduleSync, { signal });
    root.addEventListener('change', this.#scheduleSync, { signal });
    root.addEventListener('click', this.#scheduleSync, { signal });

    const observeTarget = root instanceof Document ? document.body : root;
    if (observeTarget) {
      this.#observer = new MutationObserver(this.#scheduleSync);
      this.#observer.observe(observeTarget, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'disabled', 'value'],
      });
    }

    this.#sync();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#observer?.disconnect();
    this.#cancelOptionalRestore();
    this.#restoreSaveArea();
  }

  get #profileSelect() {
    return /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-blouse-profile-select]'));
  }

  get #profileNameInput() {
    const root = this.#saveArea || this;
    return /** @type {HTMLInputElement | null} */ (root.querySelector('[data-blouse-profile-name]'));
  }

  get #saveButton() {
    const root = this.#saveArea || this;
    return /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-blouse-profile-save]'));
  }

  get #deleteButton() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-blouse-profile-delete]'));
  }

  get #message() {
    const root = this.#saveArea || this;
    return /** @type {HTMLElement | null} */ (root.querySelector('[data-blouse-profile-message]'));
  }

  get #saveArea() {
    return this.#saveAreaElement;
  }

  get #ownControls() {
    const controls = Array.from(this.querySelectorAll('input, button, select'));
    const saveArea = this.#saveArea;

    if (saveArea && !this.contains(saveArea)) {
      controls.push(...Array.from(saveArea.querySelectorAll('input, button, select')));
    }

    return controls;
  }

  get #searchRoot() {
    return this.closest('.shopify-section') || document;
  }

  #scheduleSync = () => {
    if (this.#syncQueued) return;

    this.#syncQueued = true;
    requestAnimationFrame(() => {
      this.#syncQueued = false;
      this.#sync();
    });
  };

  #sync() {
    this.#polishStitchingOption();
    const fields = this.#findProfileFields();
    const active = this.#requiredFieldCount(fields) >= MIN_VISIBLE_FIELDS;

    this.toggleAttribute('hidden', !active);
    this.dataset.active = active.toString();
    this.dataset.detectedFields = fields.size.toString();
    this.#positionSaveArea(fields, active);
    this.#setOwnControlsDisabled(!active);
    this.#updateDeleteButton();

    if (!active) {
      this.#setMessage('');
    }
  }

  #polishStitchingOption() {
    for (const wrapper of this.#searchRoot.querySelectorAll('.po-checkbox-root')) {
      const field = wrapper.querySelector('.po-field-root');
      const heading = field?.querySelector('.po-label-labelContent');
      const checkbox = field?.querySelector('input[type="checkbox"]');
      if (!field || !heading || !checkbox) continue;
      if (!field.classList.contains('geethas-blouse-option') && !/^custom blouse\??$/i.test(heading.textContent.trim())) continue;

      if (!field.classList.contains('geethas-blouse-option')) field.classList.add('geethas-blouse-option');
      if (heading.textContent !== this.dataset.stitchingHeading) heading.textContent = this.dataset.stitchingHeading;

      const label = field.querySelector('.po-optionItemLabel-label');
      if (label && label.textContent !== this.dataset.stitchingLabel) label.textContent = this.dataset.stitchingLabel;

      const price = field.querySelector('.po-optionItemLabel-price');
      if (price?.textContent.trim()) {
        const amount = price.textContent.trim().replace(/^\(/, '').replace(/\)$/, '').replace(/^\+/, '').trim();
        const currency = this.dataset.currency;
        const display = `+${amount}${amount.includes(currency) ? '' : ` ${currency}`}`;
        if (price.textContent !== display) price.textContent = display;
      }

      if (!field.querySelector('.geethas-blouse-option__note')) {
        const note = document.createElement('p');
        note.className = 'geethas-blouse-option__note';
        note.textContent = this.dataset.stitchingNote;
        field.append(note);
      }

      const helpTemplate = this.querySelector('[data-blouse-help-template]');
      if (helpTemplate && !field.querySelector('.geethas-blouse-help')) {
        field.append(helpTemplate.content.cloneNode(true));
      }
    }
  }

  #findProfileFields() {
    const controls = this.#queryControls(FIELD_SELECTOR, false).filter((control) => {
      return this.#isVisible(control) && !control.disabled;
    });
    const fields = new Map();
    const usedControls = new Set();

    for (const fieldDefinition of PROFILE_FIELDS) {
      const directMatch = controls.find((control) => {
        return !usedControls.has(control) && this.#matchesProfileField(control, fieldDefinition, false);
      });
      const nearbyMatch = directMatch || controls.find((control) => {
        return !usedControls.has(control) && this.#matchesProfileField(control, fieldDefinition, true);
      });

      if (nearbyMatch) {
        fields.set(fieldDefinition.name, nearbyMatch);
        usedControls.add(nearbyMatch);
      }
    }

    return fields;
  }

  #findOptionalProfileFields() {
    const controls = this.#queryControls(FIELD_SELECTOR, false).filter((control) => {
      return this.#isVisible(control) && !control.disabled;
    });
    const fields = new Map();
    const usedControls = new Set();

    for (const fieldDefinition of OPTIONAL_PROFILE_FIELDS) {
      const directMatch = controls.find((control) => {
        return !usedControls.has(control) && this.#matchesProfileField(control, fieldDefinition, false);
      });
      const nearbyMatch = directMatch || controls.find((control) => {
        return !usedControls.has(control) && this.#matchesProfileField(control, fieldDefinition, true);
      });

      if (nearbyMatch) {
        fields.set(fieldDefinition.name, nearbyMatch);
        usedControls.add(nearbyMatch);
      }
    }

    return fields;
  }

  #findSavedProfileFields() {
    return new Map([...this.#findProfileFields(), ...this.#findOptionalProfileFields()]);
  }

  #findPlacementOnlyFields() {
    const controls = this.#queryControls(PLACEMENT_FIELD_SELECTOR, false).filter((control) => {
      return this.#isVisible(control) && !control.disabled;
    });
    const fields = new Map();
    const usedControls = new Set();

    for (const fieldDefinition of PLACEMENT_ONLY_FIELDS) {
      const directMatch = controls.find((control) => {
        return !usedControls.has(control) && this.#matchesProfileField(control, fieldDefinition, false);
      });
      const nearbyMatch = directMatch || controls.find((control) => {
        return !usedControls.has(control) && this.#matchesProfileField(control, fieldDefinition, true);
      });

      if (nearbyMatch) {
        fields.set(fieldDefinition.name, nearbyMatch);
        usedControls.add(nearbyMatch);
      }
    }

    return fields;
  }

  /** @param {Map<string, Element>} fields */
  #requiredFieldCount(fields) {
    return PROFILE_FIELDS.reduce((count, fieldDefinition) => {
      if (fieldDefinition.optional) return count;
      return fields.has(fieldDefinition.name) ? count + 1 : count;
    }, 0);
  }

  /**
   * @param {Map<string, Element>} fields
   * @param {boolean} active
   */
  #positionSaveArea(fields, active) {
    const saveArea = this.#saveArea;
    if (!saveArea) return;

    saveArea.dataset.active = active.toString();

    const target = active ? this.#findSaveAreaTarget(fields) : null;
    if (!target?.parentElement || !target.isConnected) {
      saveArea.toggleAttribute('hidden', true);
      return;
    }

    if (target.nextElementSibling !== saveArea) {
      target.insertAdjacentElement('afterend', saveArea);
    }

    saveArea.toggleAttribute('hidden', false);
  }

  /** @param {Map<string, Element>} fields */
  #findSaveAreaTarget(fields) {
    /** @type {Element | null} */
    let latestProfileTarget = null;

    for (const [fieldName, control] of fields) {
      const fieldDefinition = PROFILE_FIELDS.find((definition) => definition.name === fieldName);
      if (!fieldDefinition) continue;

      const target = this.#placementElementForField(control, fieldDefinition);
      if (!target?.isConnected) continue;

      if (
        !latestProfileTarget ||
        latestProfileTarget.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING
      ) {
        latestProfileTarget = target;
      }
    }

    if (!latestProfileTarget) return null;

    let latestTarget = latestProfileTarget;

    for (const [fieldName, control] of this.#findPlacementOnlyFields()) {
      const fieldDefinition = PLACEMENT_ONLY_FIELDS.find((definition) => definition.name === fieldName);
      if (!fieldDefinition) continue;

      const target = this.#placementElementForPlacementField(control, fieldDefinition, latestProfileTarget);
      if (!target?.isConnected) continue;

      if (latestTarget.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING) {
        latestTarget = target;
      }
    }

    return latestTarget;
  }

  /**
   * @param {Element} control
   * @param {{name: string, aliases: string[], optional?: boolean}} fieldDefinition
   */
  #placementElementForField(control, fieldDefinition) {
    // Easify fields contain several nested wrappers inside a narrow option column.
    // Insert beside the entire option so the save panel uses the options grid's width.
    const option = control.closest('.po-option-root');
    if (option?.parentElement?.matches('.po-options-root')) return option;

    const acceptedLabels = [fieldDefinition.name, ...fieldDefinition.aliases].map((label) => this.#normalize(label));
    /** @type {Element} */
    let best = control.parentElement || control;
    let parent = control.parentElement;

    for (let depth = 0; parent && depth < 6; depth += 1) {
      if (this.contains(parent) || this.#saveArea?.contains(parent) || parent === document.body) break;

      const text = this.#normalize(parent.textContent || '');
      const hasFieldLabel = acceptedLabels.some((label) => this.#containsPhrase(text, label));
      const controlCount = parent.querySelectorAll(FIELD_SELECTOR).length;

      if (hasFieldLabel && controlCount <= 4 && text.length <= 700) {
        best = parent;
      }

      parent = parent.parentElement;
    }

    return best;
  }

  /**
   * Promotes a placement-only control to the layout level used by the final profile field.
   * It prefers the ancestor whose parent is the working profile target's parent, then falls
   * back to the outermost bounded field wrapper before their common container.
   *
   * @param {Element} control
   * @param {{name: string, aliases: string[]}} fieldDefinition
   * @param {Element} referenceTarget
   */
  #placementElementForPlacementField(control, fieldDefinition, referenceTarget) {
    const option = control.closest('.po-option-root');
    if (option?.parentElement === referenceTarget.parentElement) return option;

    const acceptedLabels = [fieldDefinition.name, ...fieldDefinition.aliases].map((label) => this.#normalize(label));
    const referenceParent = referenceTarget.parentElement;
    /** @type {Element | null} */
    let best = null;
    let parent = control.parentElement;

    for (let depth = 0; parent && depth < 8; depth += 1) {
      if (this.contains(parent) || this.#saveArea?.contains(parent) || parent === document.body) break;
      if (parent === referenceParent || parent.contains(referenceTarget)) break;

      if (referenceParent && parent.parentElement === referenceParent) {
        return parent;
      }

      const text = this.#normalize(parent.textContent || '');
      const hasFieldLabel = acceptedLabels.some((label) => this.#containsPhrase(text, label));
      const controlCount = parent.querySelectorAll(PLACEMENT_FIELD_SELECTOR).length;

      if (hasFieldLabel && controlCount <= 12 && text.length <= 700) {
        best = parent;
      }

      parent = parent.parentElement;
    }

    return best;
  }

  #restoreSaveArea() {
    const saveArea = this.#saveArea;
    const placeholder = this.#saveAreaPlaceholder;

    if (saveArea && placeholder && saveArea.parentElement !== placeholder) {
      placeholder.appendChild(saveArea);
    }
  }

  #handleProfileSelect = () => {
    const profileName = this.#profileSelect?.value;
    if (!profileName) {
      this.#updateDeleteButton();
      return;
    }

    const profile = this.#profiles.find((item) => item.name === profileName);
    if (!profile) return;

    this.#applyProfile(profile);
    this.#updateDeleteButton();
  };

  /** @param {KeyboardEvent} event */
  #handleProfileNameKeydown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.#handleSaveProfile();
    }
  };

  #handleSaveProfile = () => {
    const profileName = this.#profileNameInput?.value.trim() || '';
    if (!profileName) {
      this.#setMessage(this.dataset.nameRequired || 'Enter a profile name before saving.');
      return;
    }

    const fields = this.#findSavedProfileFields();
    const measurements = {};
    let capturedCount = 0;
    let filledCount = 0;

    for (const fieldDefinition of SAVED_PROFILE_FIELDS) {
      const field = fields.get(fieldDefinition.name);
      if (!field) continue;

      const value = this.#getControlValue(field);
      measurements[fieldDefinition.name] = value;
      capturedCount += 1;
      if (value.trim() !== '') filledCount += 1;
    }

    if (capturedCount === 0) {
      this.#setMessage(this.dataset.showFields || 'Show the Easify measurement fields before saving a profile.');
      return;
    }

    if (filledCount === 0) {
      this.#setMessage(this.dataset.empty || 'Enter at least one measurement or style selection before saving.');
      return;
    }

    const normalizedName = this.#normalize(profileName);
    this.#profiles = this.#profiles.filter((profile) => this.#normalize(profile.name) !== normalizedName);
    this.#profiles.push({
      name: profileName,
      measurements,
      savedAt: new Date().toISOString(),
    });
    this.#profiles.sort((a, b) => a.name.localeCompare(b.name));

    if (!this.#saveProfiles()) return;

    this.#renderProfileOptions(profileName);
    if (this.#profileNameInput) this.#profileNameInput.value = '';
    this.#setMessage(this.#profileMessage('saved', profileName, filledCount));
  };

  #handleDeleteProfile = () => {
    const profileName = this.#profileSelect?.value;
    if (!profileName) return;

    this.#profiles = this.#profiles.filter((profile) => profile.name !== profileName);
    if (!this.#saveProfiles()) return;

    this.#renderProfileOptions();
    this.#setMessage(this.#profileMessage('deleted', profileName));
  };

  /** @param {{name: string, measurements: Record<string, string>}} profile */
  #applyProfile(profile) {
    this.#cancelOptionalRestore();

    const fields = this.#findSavedProfileFields();
    const deferredRestores = [];
    let filledCount = 0;

    for (const fieldDefinition of SAVED_PROFILE_FIELDS) {
      const field = fields.get(fieldDefinition.name);
      const value = profile.measurements?.[fieldDefinition.name];
      if (!field || value === undefined) continue;

      if (!this.#setControlValue(field, value)) continue;

      const deferEvents = OPTIONAL_PROFILE_FIELDS.some((definition) => definition.name === fieldDefinition.name);
      if (deferEvents) {
        deferredRestores.push({ fieldName: fieldDefinition.name, value });
      } else {
        this.#dispatchValueEvents(field);
      }
      filledCount += 1;
    }

    this.#scheduleOptionalRestore(deferredRestores);
    this.#setMessage(this.#profileMessage('filled', profile.name, filledCount));
  }

  /** @param {{fieldName: string, value: string}[]} restores */
  #scheduleOptionalRestore(restores) {
    if (restores.length === 0) return;

    this.#optionalRestoreFrame = requestAnimationFrame(() => {
      this.#optionalRestoreFrame = undefined;
      const currentFields = this.#findOptionalProfileFields();

      for (const { fieldName, value } of restores) {
        const field = currentFields.get(fieldName);
        if (!field || !this.#setControlValue(field, value)) continue;

        this.#dispatchValueEvents(field);
      }
    });
  }

  #cancelOptionalRestore() {
    if (this.#optionalRestoreFrame === undefined) return;

    cancelAnimationFrame(this.#optionalRestoreFrame);
    this.#optionalRestoreFrame = undefined;
  }

  /** @param {string} [selectedProfileName] */
  #renderProfileOptions(selectedProfileName = '') {
    const select = this.#profileSelect;
    if (!select) return;

    select.replaceChildren();
    select.appendChild(new Option(this.dataset.choose || 'Choose a saved profile', ''));

    for (const profile of this.#profiles) {
      select.appendChild(new Option(profile.name, profile.name));
    }

    select.value = selectedProfileName;
    select.disabled = this.#profiles.length === 0 || this.hidden;
    this.#updateDeleteButton();
  }

  #updateDeleteButton() {
    if (this.#deleteButton) {
      this.#deleteButton.disabled = this.hidden || !this.#profileSelect?.value;
    }
  }

  /** @param {boolean} disabled */
  #setOwnControlsDisabled(disabled) {
    for (const control of this.#ownControls) {
      if (control === this.#profileSelect) continue;

      if (control instanceof HTMLInputElement || control instanceof HTMLButtonElement || control instanceof HTMLSelectElement) {
        control.disabled = disabled;
      }
    }

    if (this.#profileSelect) {
      this.#profileSelect.disabled = disabled || this.#profiles.length === 0;
    }
  }

  #loadProfiles() {
    try {
      const rawProfiles = localStorage.getItem(this.#storageKey);
      const profiles = rawProfiles ? JSON.parse(rawProfiles) : [];
      if (!Array.isArray(profiles)) return [];

      return profiles.filter((profile) => {
        return profile && typeof profile.name === 'string' && profile.measurements && typeof profile.measurements === 'object';
      });
    } catch (error) {
      return [];
    }
  }

  #saveProfiles() {
    try {
      localStorage.setItem(this.#storageKey, JSON.stringify(this.#profiles));
      return true;
    } catch (error) {
      this.#setMessage(this.dataset.unavailable || 'Saved profiles are unavailable in this browser.');
      return false;
    }
  }

  get #storageKey() {
    return this.dataset.storageKey || PROFILE_STORAGE_KEY;
  }

  /**
   * @param {Element} control
   * @param {{name: string, aliases: string[], optional?: boolean}} fieldDefinition
   * @param {boolean} includeNearbyText
   */
  #matchesProfileField(control, fieldDefinition, includeNearbyText) {
    const sources = includeNearbyText ? this.#contextTextSources(control) : this.#directTextSources(control);
    return sources.some((source) => this.#textMatchesProfileField(source, fieldDefinition));
  }

  /**
   * @param {string} text
   * @param {{name: string, aliases: string[], optional?: boolean}} fieldDefinition
   */
  #textMatchesProfileField(text, fieldDefinition) {
    const normalizedText = this.#normalize(text);
    const acceptedLabels = [fieldDefinition.name, ...fieldDefinition.aliases].map((label) => this.#normalize(label));

    if (!acceptedLabels.some((label) => this.#containsPhrase(normalizedText, label))) return false;

    return !PROFILE_FIELDS.some((otherField) => {
      if (otherField.name === fieldDefinition.name) return false;

      const otherLabels = [otherField.name, ...otherField.aliases].map((label) => this.#normalize(label));
      return otherLabels.some((otherLabel) => {
        return acceptedLabels.some((acceptedLabel) => {
          return otherLabel !== acceptedLabel && otherLabel.includes(acceptedLabel) && this.#containsPhrase(normalizedText, otherLabel);
        });
      });
    });
  }

  /** @param {Element} control */
  #directTextSources(control) {
    const sources = [
      control.getAttribute('aria-label'),
      control.getAttribute('placeholder'),
      control.getAttribute('name'),
      control.getAttribute('id'),
      control.getAttribute('data-name'),
      control.getAttribute('data-label'),
    ];

    sources.push(...this.#associatedLabelText(control));

    // Easify uses separate divs for labels and help text, with no intervening
    // text node. Read its label separately so "Chest" isn't read as "ChestAround...".
    const appLabel = control.closest('.po-field-root')?.querySelector('.po-label-root');
    if (appLabel) sources.push(appLabel.textContent);

    const previousElement = control.previousElementSibling;
    if (previousElement) sources.push(previousElement.textContent);

    const parentLabel = control.closest('label');
    if (parentLabel) sources.push(parentLabel.textContent);

    const parent = control.parentElement;
    const parentLabelText = parent?.querySelector('label')?.textContent;
    if (parentLabelText) sources.push(parentLabelText);

    return sources.filter((source) => typeof source === 'string' && source.trim() !== '');
  }

  /** @param {Element} control */
  #contextTextSources(control) {
    const sources = this.#directTextSources(control);
    let parent = control.parentElement;

    for (let depth = 0; parent && depth < 4; depth += 1) {
      if (this.contains(parent) || this.#saveArea?.contains(parent)) break;

      const text = parent.textContent || '';
      if (text.trim() !== '' && text.length <= 350) {
        sources.push(text);
      }

      parent = parent.parentElement;
    }

    return sources;
  }

  /** @param {Element} control */
  #associatedLabelText(control) {
    const labels = [];

    if (control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement || control instanceof HTMLSelectElement) {
      labels.push(...Array.from(control.labels || []).map((label) => label.textContent || ''));
    }

    const id = control.getAttribute('id');
    if (id) {
      const escapedId = window.CSS?.escape ? CSS.escape(id) : id.replace(/"/g, '\"');
      const explicitLabel = this.#searchRoot.querySelector(`label[for="${escapedId}"]`);
      if (explicitLabel) labels.push(explicitLabel.textContent || '');
    }

    return labels.filter((label) => label.trim() !== '');
  }

  /**
   * @param {string} selector
   * @param {boolean} includeHidden
   */
  #queryControls(selector, includeHidden) {
    const saveArea = this.#saveArea;
    const isOwnControl = (control) => this.contains(control) || Boolean(saveArea?.contains(control));
    const rootControls = Array.from(this.#searchRoot.querySelectorAll(selector)).filter((control) => !isOwnControl(control));
    const documentControls = Array.from(document.querySelectorAll(selector)).filter((control) => !isOwnControl(control));
    const controls = rootControls.length > 0 ? rootControls : documentControls;

    return /** @type {Array<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | HTMLButtonElement | HTMLElement>} */ (
      controls.filter((control) => {
        return includeHidden || this.#isVisible(control);
      })
    );
  }

  /** @param {Element} element */
  #isVisible(element) {
    if (element.closest('[hidden], [aria-hidden="true"]')) return false;

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;

    return element.getClientRects().length > 0;
  }

  /** @param {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | HTMLElement} control */
  #getControlValue(control) {
    if (control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement || control instanceof HTMLSelectElement) {
      return control.value;
    }

    return control.textContent?.trim() || '';
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | HTMLElement} control
   * @param {string} value
   * @returns {boolean}
   */
  #setControlValue(control, value) {
    if (control instanceof HTMLSelectElement) {
      const matchingOption = Array.from(control.options).find((option) => {
        return option.value === value || this.#normalize(option.textContent || '') === this.#normalize(value);
      });
      if (!matchingOption) return false;

      const valueSetter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set;
      if (valueSetter) {
        valueSetter.call(control, matchingOption.value);
      } else {
        control.value = matchingOption.value;
      }
      return true;
    }

    if (control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement) {
      const valueSetter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(control), 'value')?.set;
      if (valueSetter) {
        valueSetter.call(control, value);
      } else {
        control.value = value;
      }
      return true;
    }

    return false;
  }

  /** @param {Element} control */
  #dispatchValueEvents(control) {
    control.dispatchEvent(new Event('input', { bubbles: true }));
    control.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * @param {string} key
   * @param {string} name
   * @param {number} [count]
   */
  #profileMessage(key, name, count = 0) {
    const fallback = { saved: 'Saved "[name]" with [count] fields.', deleted: 'Deleted "[name]".', filled: 'Filled [count] fields from "[name]".' };
    return (this.dataset[key] || fallback[key]).replace('[count]', String(count)).replace('[name]', () => name);
  }

  /** @param {string} message */
  #setMessage(message) {
    if (this.#message) this.#message.textContent = message;
  }

  /**
   * @param {string} text
   * @param {string} phrase
   */
  #containsPhrase(text, phrase) {
    return new RegExp(`(^|[^a-z0-9])${this.#escapeRegExp(phrase)}($|[^a-z0-9])`).test(text);
  }

  /** @param {string} value */
  #normalize(value) {
    return value
      .toString()
      .toLowerCase()
      .replace(/[\[\]{}():_\-/]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /** @param {string} value */
  #escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}

if (!customElements.get('blouse-measurements')) {
  customElements.define('blouse-measurements', BlouseMeasurements);
}



